import type { Tag } from "services/tags/model";

type S3Object = {
  key: string;
  prefix: string;
  name: string;
  isPrivate: boolean;
  libraryObject?: LibraryObject;
};

export const FolderRootType = {
  ADMIN_CONTENT: "admin-content",
  EXERCISE: "exercise",
} as const;

export type FolderRootType = (typeof FolderRootType)[keyof typeof FolderRootType];

export type Folder = S3Object;

export type File = S3Object & {
  lastModified?: Date;
};

export type FolderContents = { folders: Folder[]; files: File[] };

export const LibraryObjectType = {
  COURSE: "course",
  LESSON: "lesson",
  FOLDER: "folder",
} as const;

export type LibraryObjectType =
  (typeof LibraryObjectType)[keyof typeof LibraryObjectType];

export type LibraryObject = {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  preview_url?: string;
  preview_vertical_url?: string;
  preview_horizontal_url?: string;
  preview?: globalThis.File | File;
  preview_vertical?: globalThis.File | File;
  preview_horizontal?: globalThis.File | File;
  author?: string | null;
  order?: number | null;
  parent?: LibraryObject | LibraryObjectParent | { id: string } | null;
  length?: number | null;
  subtype?: string | null;
  readings?: number | null;
  videos?: number | null;
  tags?: Tag[];
  type?: LibraryObjectType;
  icon_url?: string;
  tier?: string;
  progress?: {
    progress: number;
    completed: Array<{ id: string; length?: number | null }>;
    remaining: Array<{ id: string; length?: number | null }>;
  } | null;
  children?: {
    lessons: number;
    "case-studies": number;
    projects: number;
  };
  is_favorite?: boolean;
  exercises?: ShortExercise[];
};

export function blankLibraryObject(): LibraryObject {
  return { id: "", key: "", name: "" };
}

/** An exercise as the library endpoints list it, without its content or metadata. */
export type ShortExercise = { id: string; name: string; type: string };

/**
 * `GET /library/{id}`, which answers more than the list endpoints do: the object's own
 * children, and a parent expanded one level with its children as bare ids.
 */
export type LibraryObjectDetail = Omit<LibraryObject, "parent" | "exercises"> & {
  // The schema defaults both to empty rather than omitting them.
  exercises: ShortExercise[];
  list: LibraryObject[];
  // Always the expanded form here, unlike the bare id a list response carries.
  parent?: LibraryObjectParent | null;
};

// The parent as the detail endpoint expands it, all the way up: every ancestor
// carries its own children as bare ids and its own parent in this same shape.
export type LibraryObjectParent = Omit<LibraryObject, "parent"> & {
  parent?: LibraryObjectParent | null;
  list: Array<{ id: string; name?: string | null }>;
};

type EditorHierarchy = {
  id: string;
  name: string;
  type: "object" | "version";
  editor_hierarchy: EditorHierarchy | null;
};

export type AdminLibraryObject = LibraryObject & { editor_hierarchy: EditorHierarchy };

export type LibraryVersion = {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  preview_url?: string;
  preview_vertical_url?: string;
  preview_horizontal_url?: string;
  preview?: globalThis.File | File;
  preview_vertical?: globalThis.File | File;
  preview_horizontal?: globalThis.File | File;
  author?: string | null;
  order?: number | null;
  editor_hierarchy: EditorHierarchy;
  length?: number | null;
  subtype?: string | null;
  readings?: number | null;
  videos?: number | null;
  type: LibraryObjectType;
  tags: Tag[];
  original?: { id: string };
  icon_url?: string;
  tier?: string | null;
  // Background S3 copy state (admin only). Null for blank drafts (nothing copied).
  content_copy_status?: "queued" | "running" | "succeeded" | "failed" | null;
  content_copy_error?: string | null;
};
